// src/services/api/notifications.ts

import { apiCweb } from './api-cweb';
import { appLog } from '~src/utils/log';

// Type definitions
export type DbOfferNotificationTemplate = {
  id: number;
  title: string;
  description: string;
};

export type DbOfferNotificationRecipient = {
  id: number;
  email: string;
  state: number; // 1=pending, 2=sent, 3=delivered, 4=failed
  created_at: number;
  mailed_at: number;
  notification_template_title: string;
  notification__template_description: string; // Note: API has double underscore
  share_key: string;
  share_key_title: string; // Vendor name
  recipient_email_language_code: string;
  offer_id: number;
};

type TemplateParams = {
  projectId: number;
  tenderId: number;
};

type TemplateUpdateParams = TemplateParams & {
  title: string;
  description: string;
};

type PreviewParams = TemplateUpdateParams;

type RecipientsParams = TemplateParams & {
  skip?: number;
  take?: number;
};

// =================================== OFFER FINALIZATION NOTIFICATION

// Get offer finalization receipt notification template
export const getOfferFinalizationTemplate = async ({
  projectId,
  tenderId,
}: TemplateParams): Promise<DbOfferNotificationTemplate> => {
  const url = `/projects/${projectId}/tenders/${tenderId}/offer_finalization_receipt_notification/template`;
  appLog('getOfferFinalizationTemplate ->', url);

  const response = await apiCweb.get(url);
  appLog('getOfferFinalizationTemplate #2 ->', response.data);
  return response.data;
};

// Update offer finalization receipt notification template
export const updateOfferFinalizationTemplate = async ({
  projectId,
  tenderId,
  title,
  description,
}: TemplateUpdateParams): Promise<void> => {
  const url = `/projects/${projectId}/tenders/${tenderId}/offer_finalization_receipt_notification/template`;
  appLog('updateOfferFinalizationTemplate ->', url, { title, description });

  await apiCweb.put(url, { title, description });
  appLog('updateOfferFinalizationTemplate #2 -> success');
};

// Get offer finalization receipt notification preview HTML
export const getOfferFinalizationPreview = async ({
  projectId,
  tenderId,
  title,
  description,
}: PreviewParams): Promise<string> => {
  const url = `/projects/${projectId}/tenders/${tenderId}/offer_finalization_receipt_notification/template_preview_as_html`;
  appLog('getOfferFinalizationPreview ->', url, { title, description });

  const response = await apiCweb.post(
    url,
    { title, description },
    { headers: { Accept: 'text/html' } }
  );
  appLog('getOfferFinalizationPreview #2 -> HTML received');
  return response.data;
};

// Get offer finalization receipt notification recipients
export const getOfferFinalizationRecipients = async ({
  projectId,
  tenderId,
  skip = 0,
  take = 100,
}: RecipientsParams): Promise<DbOfferNotificationRecipient[]> => {
  const url = `/projects/${projectId}/tenders/${tenderId}/offer_finalization_receipt_notification/recipients`;
  appLog('getOfferFinalizationRecipients ->', url, { skip, take });

  const response = await apiCweb.get(url, {
    params: { skip, take },
  });
  appLog('getOfferFinalizationRecipients #2 ->', response.data);
  // IMPORTANT: API returns array directly, NOT { items, totalCount }
  return response.data;
};

// =================================== OFFER REOPEN NOTIFICATION

// Get offer reopen receipt notification template
export const getOfferReopenTemplate = async ({
  projectId,
  tenderId,
}: TemplateParams): Promise<DbOfferNotificationTemplate> => {
  const url = `/projects/${projectId}/tenders/${tenderId}/offer_reopen_receipt_notification/template`;
  appLog('getOfferReopenTemplate ->', url);

  const response = await apiCweb.get(url);
  appLog('getOfferReopenTemplate #2 ->', response.data);
  return response.data;
};

// Update offer reopen receipt notification template
export const updateOfferReopenTemplate = async ({
  projectId,
  tenderId,
  title,
  description,
}: TemplateUpdateParams): Promise<void> => {
  const url = `/projects/${projectId}/tenders/${tenderId}/offer_reopen_receipt_notification/template`;
  appLog('updateOfferReopenTemplate ->', url, { title, description });

  await apiCweb.put(url, { title, description });
  appLog('updateOfferReopenTemplate #2 -> success');
};

// Get offer reopen receipt notification preview HTML
export const getOfferReopenPreview = async ({
  projectId,
  tenderId,
  title,
  description,
}: PreviewParams): Promise<string> => {
  const url = `/projects/${projectId}/tenders/${tenderId}/offer_reopen_receipt_notification/template_preview_as_html`;
  appLog('getOfferReopenPreview ->', url, { title, description });

  const response = await apiCweb.post(
    url,
    { title, description },
    { headers: { Accept: 'text/html' } }
  );
  appLog('getOfferReopenPreview #2 -> HTML received');
  return response.data;
};

// Get offer reopen receipt notification recipients
export const getOfferReopenRecipients = async ({
  projectId,
  tenderId,
  skip = 0,
  take = 100,
}: RecipientsParams): Promise<DbOfferNotificationRecipient[]> => {
  const url = `/projects/${projectId}/tenders/${tenderId}/offer_reopen_receipt_notification/recipients`;
  appLog('getOfferReopenRecipients ->', url, { skip, take });

  const response = await apiCweb.get(url, {
    params: { skip, take },
  });
  appLog('getOfferReopenRecipients #2 ->', response.data);
  // IMPORTANT: API returns array directly, NOT { items, totalCount }
  return response.data;
};
